import { Typography } from '@/components/atoms';
import { CheckIcon } from '@/components/atoms/icons/CheckIcon';
import { ChevronDownIcon } from '@/components/atoms/icons/ChevronDownIcon';
import { ChevronRightIcon } from '@/components/atoms/icons/ChevronRightIcon';
import { SearchIcon } from '@/components/atoms/icons/SearchIcon';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useTranslation, useUIColors, useWebViewport } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { useAppStore } from '@/core/stores/appStore';
import { BREAKPOINTS } from '@/core/utils/responsive';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, TextInput, View, type TextStyle } from 'react-native';
import {
  toAvailableUnit,
  useUnitNumberSearch,
  type SearchableUnit,
} from '../hooks/useUnitNumberSearch';
import {
  BEDROOM_FILTER_OPTIONS,
  isSameBedroomSelection,
  resolveUnitBedroomOption,
  type BedroomFilterOption,
} from '../utils/unitListingFilters';
import { OverlayScrollView } from './OverlayScrollView';

const MATCH_ROW_HEIGHT = 60;
const VISIBLE_MATCH_ROWS = 3;
const FIELD_HEIGHT = 48;

type ColorTokens = ReturnType<typeof useUIColors>;
type TranslateFn = ReturnType<typeof useTranslation>['t'];

export interface UnitListingInlineFiltersProps {
  /** Units already on the listing (price-filtered, e.g. first load at 1.7M). */
  units: readonly SearchableUnit[];
  appliedBedrooms: readonly BedroomFilterOption[];
  onApplyBedrooms: (bedrooms: readonly BedroomFilterOption[]) => void;
  onUnitPress: (unitId: string) => void;
  projectName: string;
}

/** Closes a dropdown on a pointer press outside `containerRef` or on Escape. */
function useDismissOnOutsidePress(
  containerRef: React.RefObject<View | null>,
  isOpen: boolean,
  onDismiss: () => void,
): void {
  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return undefined;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const container = containerRef.current as unknown as HTMLElement | null;
      if (container && event.target instanceof Node && container.contains(event.target)) {
        return;
      }
      onDismiss();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [containerRef, isOpen, onDismiss]);
}

function bedroomShortLabel(t: TranslateFn, option: BedroomFilterOption): string {
  return t('propertiesWeb.list.filters.bedroomsShort').replace(
    '{{count}}',
    option.replace('BR', ''),
  );
}

function bedroomOptionLabel(t: TranslateFn, option: BedroomFilterOption): string {
  return t('propertiesWeb.list.filters.bedroomOption').replace(
    '{{count}}',
    option.replace('BR', ''),
  );
}

function unitSubtitle(t: TranslateFn, unit: SearchableUnit, projectName: string): string {
  const bedrooms = resolveUnitBedroomOption(unit);
  return [
    unit.project_name?.trim() || projectName,
    bedrooms ? bedroomShortLabel(t, bedrooms) : '',
    unit.unit_type?.trim() ?? '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export function UnitListingInlineFilters({
  units,
  appliedBedrooms,
  onApplyBedrooms,
  onUnitPress,
  projectName,
}: Readonly<UnitListingInlineFiltersProps>) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();
  const { breakpointWidth } = useWebViewport();
  const isPhone = breakpointWidth < BREAKPOINTS.PHONE;
  const [openDropdown, setOpenDropdown] = useState<'search' | 'bedrooms' | null>(null);

  return (
    <View style={[styles.row, isPhone && styles.rowPhone]}>
      <UnitNumberSearchField
        colors={colors}
        t={t}
        isRTL={isRTL}
        isPhone={isPhone}
        units={units}
        projectName={projectName}
        onUnitPress={onUnitPress}
        isOpen={openDropdown === 'search'}
        onOpenChange={(open) => setOpenDropdown(open ? 'search' : null)}
      />
      <BedroomDropdownField
        colors={colors}
        t={t}
        isPhone={isPhone}
        appliedBedrooms={appliedBedrooms}
        onApplyBedrooms={onApplyBedrooms}
        isOpen={openDropdown === 'bedrooms'}
        onOpenChange={(open) => setOpenDropdown(open ? 'bedrooms' : null)}
      />
    </View>
  );
}

interface UnitNumberSearchFieldProps {
  colors: ColorTokens;
  t: TranslateFn;
  isRTL: boolean;
  isPhone: boolean;
  units: readonly SearchableUnit[];
  projectName: string;
  onUnitPress: (unitId: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function UnitNumberSearchField({
  colors,
  t,
  isRTL,
  isPhone,
  units,
  projectName,
  onUnitPress,
  isOpen,
  onOpenChange,
}: Readonly<UnitNumberSearchFieldProps>) {
  const containerRef = useRef<View>(null);
  const [query, setQuery] = useState('');
  const { matches, isSearchingRemote } = useUnitNumberSearch(query, units);
  const upsertAvailableUnitFirst = useAppStore((state) => state.upsertAvailableUnitFirst);
  const hasQuery = query.trim().length > 0;
  const showMatches = isOpen && hasQuery;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismissOnOutsidePress(containerRef, isOpen, close);

  const handleChangeText = useCallback(
    (value: string) => {
      setQuery(value);
      onOpenChange(true);
    },
    [onOpenChange],
  );

  const handleUnitPress = useCallback(
    (unit: SearchableUnit) => {
      const availableUnit = toAvailableUnit(unit);
      if (availableUnit) {
        upsertAvailableUnitFirst(availableUnit);
      }
      onOpenChange(false);
      onUnitPress(unit.unit_id);
    },
    [onOpenChange, onUnitPress, upsertAvailableUnitFirst],
  );

  const inputStyle: TextStyle = {
    color: UI_PRIMITIVES.inkWarm201D1C,
    textAlign: isRTL ? 'right' : 'left',
    writingDirection: isRTL ? 'rtl' : 'ltr',
  };

  return (
    <View style={[styles.column, isOpen && styles.columnOpen]}>
      <Typography
        variant="headline"
        color={UI_PRIMITIVES.inkWarm201D1C}
        style={[styles.label, isPhone && styles.labelPhone]}
      >
        {t('propertiesWeb.list.filters.unitNumber')}
      </Typography>
      <View ref={containerRef} style={styles.fieldAnchor}>
        <View
          style={[
            styles.field,
            isPhone && styles.fieldPhone,
            {
              borderColor: hasQuery ? colors.bloomRed : colors.outlineInactive,
              backgroundColor: colors.backgroundLevel1,
            },
          ]}
        >
          <SearchIcon size={isPhone ? 16 : 18} color={colors.fontTertiary} />
          <TextInput
            value={query}
            onChangeText={handleChangeText}
            onFocus={() => onOpenChange(true)}
            placeholder={t('propertiesWeb.list.filters.unitNumberPlaceholder')}
            placeholderTextColor={UI_PRIMITIVES.greyWarm625C59}
            accessibilityLabel={t('propertiesWeb.list.filters.unitNumber')}
            autoCorrect={false}
            autoCapitalize="characters"
            style={[styles.searchInput, isPhone && styles.searchInputPhone, inputStyle]}
          />
        </View>
        {showMatches ? (
          <View
            style={[
              styles.dropdown,
              { borderColor: colors.outlineInactive, backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            <View style={[styles.matchesHeader, { backgroundColor: colors.backgroundLevel2 }]}>
              <Typography variant="caption" color="tertiary">
                {matches.length > 0 || isSearchingRemote
                  ? t('propertiesWeb.list.filters.matchingUnits')
                  : t('propertiesWeb.list.filters.noMatchingUnits')}
              </Typography>
            </View>
            <OverlayScrollView style={styles.matchesScroll}>
              {matches.map((unit) => (
                <Pressable
                  key={unit.unit_id}
                  onPress={() => handleUnitPress(unit)}
                  accessibilityRole="button"
                  accessibilityLabel={unit.unit_number}
                  style={[styles.matchRow, { borderTopColor: colors.outlineInactive }]}
                >
                  <View style={styles.matchText}>
                    <Typography
                      variant="headline"
                      color="primary"
                      style={isPhone ? styles.matchTitlePhone : undefined}
                    >
                      {unit.unit_number}
                    </Typography>
                    <Typography variant="caption" color="tertiary">
                      {unitSubtitle(t, unit, projectName)}
                    </Typography>
                  </View>
                  <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                    <ChevronRightIcon size={16} color={colors.fontPrimary} />
                  </View>
                </Pressable>
              ))}
            </OverlayScrollView>
          </View>
        ) : null}
      </View>
    </View>
  );
}

interface BedroomDropdownFieldProps {
  colors: ColorTokens;
  t: TranslateFn;
  isPhone: boolean;
  appliedBedrooms: readonly BedroomFilterOption[];
  onApplyBedrooms: (bedrooms: readonly BedroomFilterOption[]) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function BedroomDropdownField({
  colors,
  t,
  isPhone,
  appliedBedrooms,
  onApplyBedrooms,
  isOpen,
  onOpenChange,
}: Readonly<BedroomDropdownFieldProps>) {
  const containerRef = useRef<View>(null);
  // Only Apply commits the draft; reopening starts again from the applied selection.
  const [draftBedrooms, setDraftBedrooms] =
    useState<readonly BedroomFilterOption[]>(appliedBedrooms);

  useEffect(() => {
    if (isOpen) {
      setDraftBedrooms(appliedBedrooms);
    }
  }, [isOpen, appliedBedrooms]);

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  useDismissOnOutsidePress(containerRef, isOpen, close);

  const toggleBedroom = useCallback((option: BedroomFilterOption) => {
    setDraftBedrooms((current) =>
      current.includes(option) ? current.filter((value) => value !== option) : [...current, option],
    );
  }, []);

  const handleApply = useCallback(() => {
    onOpenChange(false);
    if (!isSameBedroomSelection(draftBedrooms, appliedBedrooms)) {
      onApplyBedrooms(draftBedrooms);
    }
  }, [appliedBedrooms, draftBedrooms, onApplyBedrooms, onOpenChange]);

  const appliedLabel = BEDROOM_FILTER_OPTIONS.filter((option) => appliedBedrooms.includes(option))
    .map((option) => bedroomOptionLabel(t, option))
    .join(', ');
  const hasApplied = appliedLabel.length > 0;

  return (
    <View style={[styles.column, isOpen && styles.columnOpen]}>
      <Typography
        variant="headline"
        color={UI_PRIMITIVES.inkWarm201D1C}
        style={[styles.label, isPhone && styles.labelPhone]}
      >
        {t('propertiesWeb.list.filters.numberOfBedrooms')}
      </Typography>
      <View ref={containerRef} style={styles.fieldAnchor}>
        <Pressable
          onPress={() => onOpenChange(!isOpen)}
          accessibilityRole="button"
          accessibilityLabel={t('propertiesWeb.list.filters.numberOfBedrooms')}
          accessibilityState={{ expanded: isOpen }}
          style={[
            styles.field,
            isPhone && styles.fieldPhone,
            {
              borderColor: isOpen ? colors.bloomRed : colors.outlineInactive,
              backgroundColor: colors.backgroundLevel1,
            },
          ]}
        >
          <Typography
            variant="body"
            color={hasApplied ? 'primary' : UI_PRIMITIVES.greyWarm625C59}
            numberOfLines={1}
            style={[styles.triggerText, isPhone && styles.triggerTextPhone]}
          >
            {hasApplied ? appliedLabel : t('propertiesWeb.list.filters.selectBedrooms')}
          </Typography>
          <View style={isOpen ? styles.chevronOpen : undefined}>
            <ChevronDownIcon size={isPhone ? 16 : 18} color={colors.fontPrimary} />
          </View>
        </Pressable>
        {isOpen ? (
          <View
            style={[
              styles.dropdown,
              styles.bedroomDropdown,
              { borderColor: colors.outlineInactive, backgroundColor: colors.backgroundLevel1 },
            ]}
          >
            {BEDROOM_FILTER_OPTIONS.map((option) => {
              const isSelected = draftBedrooms.includes(option);
              return (
                <Pressable
                  key={option}
                  onPress={() => toggleBedroom(option)}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked: isSelected }}
                  style={[
                    styles.optionRow,
                    isSelected && { backgroundColor: UI_PRIMITIVES.redPaleFBF3F1 },
                  ]}
                >
                  <View
                    style={[
                      styles.optionCheck,
                      isSelected
                        ? { backgroundColor: UI_PRIMITIVES.redLight, borderColor: UI_PRIMITIVES.redLight }
                        : { borderColor: colors.outlineInactive },
                    ]}
                  >
                    {isSelected ? <CheckIcon size={10} color={colors.white} /> : null}
                  </View>
                  <Typography variant="subhead" color="primary" style={styles.optionText}>
                    {bedroomOptionLabel(t, option)}
                  </Typography>
                </Pressable>
              );
            })}
            <View style={[styles.optionsDivider, { backgroundColor: colors.outlineInactive }]} />
            <View style={styles.applyRow}>
              <Pressable
                onPress={handleApply}
                accessibilityRole="button"
                accessibilityLabel={t('propertiesWeb.list.filters.apply')}
                style={styles.applyButton}
              >
                <View style={styles.applyUnderline}>
                  <Typography variant="caption" color={UI_PRIMITIVES.redLight} style={styles.applyText}>
                    {t('propertiesWeb.list.filters.apply')}
                  </Typography>
                </View>
              </Pressable>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: 16,
    width: '100%',
    zIndex: 3,
  },
  rowPhone: {
    gap: 8,
  },
  /** flex-end keeps both fields level if one label wraps. */
  column: {
    flex: 1,
    minWidth: 0,
    gap: 8,
    justifyContent: 'flex-end',
  },
  columnOpen: {
    zIndex: 4,
  },
  /** Figma SemiBold 16, LH 21, #201D1C */
  label: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '600',
    fontSize: 16,
    lineHeight: 21,
    letterSpacing: 0,
  },
  labelPhone: {
    fontSize: 13,
    lineHeight: 17,
  },
  fieldAnchor: {
    position: 'relative',
  },
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: FIELD_HEIGHT,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
  },
  fieldPhone: {
    gap: 6,
    paddingHorizontal: 10,
  },
  searchInput: {
    flex: 1,
    minWidth: 0,
    height: FIELD_HEIGHT - 2,
    // Figma Font/Size/S Regular 15, LH 100%, #201D1C
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 15,
    lineHeight: 15,
    letterSpacing: 0,
    // Web-only: the field border already shows focus.
    ...({ outlineStyle: 'none' } as object),
  },
  searchInputPhone: {
    fontSize: 13,
    lineHeight: 13,
  },
  triggerText: {
    flex: 1,
    minWidth: 0,
  },
  triggerTextPhone: {
    fontSize: 13,
    lineHeight: 17,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    borderWidth: 1,
    borderRadius: 8,
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOpacity: 0.12,
    shadowRadius: 16,
    shadowOffset: { width: 0, height: 6 },
    elevation: 6,
  },
  bedroomDropdown: {
    padding: 8,
    gap: 4,
  },
  matchesHeader: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  matchesScroll: {
    maxHeight: MATCH_ROW_HEIGHT * VISIBLE_MATCH_ROWS,
  },
  /** Long unit numbers wrap; the row grows instead of overlapping. */
  matchRow: {
    minHeight: MATCH_ROW_HEIGHT,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
  },
  matchText: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  matchTitlePhone: {
    fontSize: 14,
    lineHeight: 18,
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    minHeight: 36,
    paddingHorizontal: 8,
    borderRadius: 6,
  },
  /** Figma Regular 14, LH 18 */
  optionText: {
    fontFamily: 'Matter-Regular',
    fontWeight: '400',
    fontSize: 14,
    lineHeight: 18,
    letterSpacing: 0,
    includeFontPadding: false,
    // Matter caps sit ~2px above the line-box centre; nudge onto the checkbox centre.
    transform: [{ translateY: 2 }],
  },
  optionCheck: {
    width: 18,
    height: 18,
    borderRadius: 4,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  optionsDivider: {
    height: 1,
    width: '100%',
    marginTop: 4,
  },
  applyRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingTop: 4,
  },
  applyButton: {
    paddingHorizontal: 8,
    paddingVertical: 6,
  },
  /** Link underline drawn as a border so it sits clear of the text. */
  applyUnderline: {
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: UI_PRIMITIVES.redLight,
  },
  /** Figma Font/Size/3XS SemiBold 12, LH 100%, #922A22 */
  applyText: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: '600',
    fontSize: 12,
    lineHeight: 12,
    letterSpacing: 0,
    textAlign: 'center',
    textAlignVertical: 'center',
  },
});
